import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles, Typography } from '@material-ui/core';
import useEventsReporter from 'src/hooks/useEventsReporter';
import eventMetricsMap from 'src/hooks/useEventsReporter/event-types';
import useStoreData from 'src/hooks/useStoreData';
import { Store, StoreService } from 'src/services/types';
import { getStoresTsa } from 'src/lib/api-client/staples-stores';
import { useTabsUIStateContext } from 'src/lib/contexts/tabs-ui-state-context';
import FullWidthCard from '../cards/full-width-card';
import COLORS from '../../lib/colors';
import LoadingButton from '../loading-button';
import { openInNewTab } from '../../lib/utils/nav-utils';
import tsa_idemia_image from '../../assets/bgs/tsa_idemia.png';
import tsa_clear_image from '../../assets/bgs/tsa_clear.png';
import tsa_default_image from '../../assets/bgs/tsa_default.png';
import { Body, Button, Heading } from '../UI';

const useStyles = makeStyles(() => ({
    header: {
        '& img': {
            width: '100%',
        },
    },
    contentContainer: {
        display: 'flex',
        columnGap: '1rem',
        alignItems: 'center',
    },
    reg: {
        fontSize: '12px',
    },
    ctaBtn: {
        height: '2.5rem',
        borderRadius: '1.885rem',
        background: COLORS.homeBlack,
        color: COLORS.whiteSmoke,
        textTransform: 'none',
        fontSize: '1rem',
        '&:hover': {
            backgroundColor: COLORS.homeBlack,
        },
        '&:active': {
            backgroundColor: COLORS.homeBlack,
        },
        fontWeight: 500,
        margin: '1rem 0',
    },
    actions: {
        display: 'flex',
        // flexDirection: 'column',
        columnGap: '1rem',
        alignItems: 'center',
    },
    action: {
        color: COLORS.brandCyan,
        fontSize: '14px',
        fontWeight: 500,
        '&:hover': {
            textDecoration: 'none',
        },
    },
    tsaPre: {
        padding: '1rem',
        margin: '0px 16px 20px',
        display: 'flex',
        flexDirection: 'column',
        rowGap: '1rem',
        backgroundColor: 'white',
        borderRadius: 15,
    },
    nearestStore: {
        color: COLORS.secondaryNeutralBlack,
        fontSize: '16px',
        fontWeight: 500,
        '& span': {
            color: COLORS.brandCyan,
        },
    },
    tsaLogoImage: {
        maxWidth: '30%',
    },
}));

export function TsaPreCheck() {
    const classes = useStyles();
    const { mainStore } = useStoreData();
    const { setStoreLinkInNative } = useTabsUIStateContext();
    const [localStoreHasTsa, setLocalStoreHasTsa] = useState(false);
    const [tsaPrecheckValue, setTsaPrecheckValue] = useState<
        'DEFAULT' | 'IDEMIA' | 'CLEAR'
    >('DEFAULT');
    const [nearestStoreWithTsa, setNearestStoreWithTsa] =
        useState<Store | null>(null);
    const { analyticsCustomEvent } = useEventsReporter();
    const getStarted = `${process.env.REACT_APP_DOT_COM_URL}/stores/services/tsa`;
    const moreLocationLink = `${process.env.REACT_APP_DOT_COM_URL}/stores/services/tsa#Search-participating-locations`;
    const getStartedLink_tsa_clear = `https://tsaprecheckbyclear.tsa.dhs.gov/?affiliate=staplesapp&utm_source=app&utm_medium=staples&utm_campaign=enroll`;
    const differentLoc = `${process.env.REACT_APP_DOT_COM_URL}/stores/services/tsa#Search-participating-locations`;

    const fetchAPI = async (zipCode: any) => {
        const additionalStores: any = await getStoresTsa(zipCode);
        console.log('mainStore', additionalStores);
        const nearestStore: any =
            additionalStores?.find((store: any) =>
                (store?.storeServices as unknown as StoreService[])?.find(
                    (item) =>
                        item.id === 'tsa_precheck' ||
                        item.id === 'tsa_precheck_clear'
                )
            ) || undefined;
        return setNearestStoreWithTsa(nearestStore);
    };

    useEffect(() => {
        const is_tsa_precheck_idemia = (
            mainStore?.storeServices as unknown as StoreService[]
        )?.some((item) => item.id === 'tsa_precheck');
        const is_tsa_precheck_clear = (
            mainStore?.storeServices as unknown as StoreService[]
        )?.some((item) => item.id === 'tsa_precheck_clear');
        if (is_tsa_precheck_idemia) {
            setTsaPrecheckValue('IDEMIA');
        } else if (is_tsa_precheck_clear) {
            setTsaPrecheckValue('CLEAR');
        } else {
            setTsaPrecheckValue('DEFAULT');
        }
    }, [mainStore]);

    useEffect(() => {
        fetchAPI(mainStore?.address?.postal_code);
    }, [mainStore?.address?.address_1]);

    const tsa_precheck_memo = useMemo(() => {
        if (tsaPrecheckValue === 'IDEMIA') {
            return {
                image: tsa_idemia_image,
                getStartedLink: getStarted,
                locationLink: moreLocationLink,
                description:
                    'Your local Staples® store offers TSA PreCheck® enrollment.',
                nearestStore: false,
            };
        }
        if (tsaPrecheckValue === 'CLEAR') {
            return {
                image: tsa_clear_image,
                getStartedLink: getStartedLink_tsa_clear,
                locationLink: moreLocationLink,
                description:
                    'Your local Staples® store offers TSA PreCheck® enrollment.',
                nearestStore: false,
            };
        }
        if (tsaPrecheckValue === 'DEFAULT' && nearestStoreWithTsa) {
            if (nearestStoreWithTsa.id === 'tsa_precheck') {
                return {
                    image: tsa_idemia_image,
                    getStartedLink: getStarted,
                    locationLink: moreLocationLink,
                    description:
                        'Staples is your closest store that offers TSA PreCheck® enrollment.',
                    nearestStore: true,
                };
            }
            if (nearestStoreWithTsa.id === 'tsa_precheck_clear') {
                return {
                    image: tsa_clear_image,
                    getStartedLink: getStartedLink_tsa_clear,
                    locationLink: moreLocationLink,
                    description:
                        'Staples is your closest store that offers TSA PreCheck® enrollment.',
                    nearestStore: true,
                };
            }
            return {
                image: tsa_default_image,
                getStartedLink: getStarted,
                locationLink: moreLocationLink,
                description:
                    'TSA PreCheck® enrollment is now available at participating Staples® stores.',
                nearestStore: true,
            };
        }
        return {
            image: tsa_default_image,
            getStartedLink: getStarted,
            locationLink: moreLocationLink,
            nearestStore: false,
        };
    }, [tsaPrecheckValue, nearestStoreWithTsa]);

    return (
        <section id="tsa-precheck" className={classes.tsaPre}>
            <div className={classes.contentContainer}>
                <img
                    aria-hidden="true"
                    src={tsa_precheck_memo.image}
                    alt="protection plan"
                    className={classes.tsaLogoImage}
                />
                <div id="tsa-precheck-text">
                    <Heading type="h6" size={18}>
                        Upgrade your travel
                    </Heading>
                    {/* <CardText /> */}
                    {tsa_precheck_memo?.nearestStore ? (
                        <Body size={14} weight="normal" lineHeight={18}>
                            {nearestStoreWithTsa?.address?.city},{' '}
                            {nearestStoreWithTsa?.address?.region}
                        </Body>
                    ) : null}
                    <Body size={14} weight="light" lineHeight={18}>
                        {tsa_precheck_memo.description}
                    </Body>
                </div>
            </div>
            <div className={classes.actions}>
                <Button
                    id="tsa-pre-get-started"
                    variant="contained-rounded"
                    bgColor="bgBlack"
                    color="textWhite"
                    onClick={() => {
                        setStoreLinkInNative({
                            src: tsa_precheck_memo.getStartedLink,
                            show: true,
                        });
                        analyticsCustomEvent(eventMetricsMap.stores, {
                            click_text: 'apply now',
                            element_location: 'promotion tiles',
                            tile_category: 'tsa carousel',
                        });
                    }}
                >
                    <Body size={14}>Get started</Body>
                </Button>
                <Button
                    id="tsa-pre-different-loc"
                    variant="text"
                    color="textLinkBlue"
                    onClick={() => {
                        setStoreLinkInNative({
                            src: tsa_precheck_memo.locationLink,
                            show: true,
                        });
                        analyticsCustomEvent(eventMetricsMap.stores, {
                            click_text: 'find different location',
                            element_location: 'promotion tiles',
                            tile_category: 'tsa carousel',
                        });
                    }}
                >
                    <Body size={14}>More locations</Body>
                </Button>
            </div>
        </section>
    );
}
export default TsaPreCheck;
